import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  GenderOption,
  WhoopBodyMeasurement,
  WhoopCalibrationService,
  WhoopProfile,
  WhoopWorkoutItem,
  WhoopWorkoutResponse
} from '../../gen2ai/whoop-calibration.service';

interface TrackerResults {
  bmi: number;
  bodyFatPercent: number;
  usedEstimatedBodyFat: boolean;
  leanMassLbs: number;
  fatMassLbs: number;
  walkCaloriesBase: number;
  runCaloriesBase: number;
  totalCaloriesBase: number;
  walkCaloriesAdjusted: number;
  runCaloriesAdjusted: number;
  totalCaloriesAdjusted: number;
  recalibrationFactor: number;
}

@Component({
  selector: 'app-body-mass-tracker',
  templateUrl: './body-mass-tracker.component.html',
  styleUrls: ['./body-mass-tracker.component.scss']
})
export class BodyMassTrackerComponent implements OnInit, OnDestroy {
  heightFeet = 5;
  heightInches = 8;
  weightLbs = 170;
  age = 35;
  gender: GenderOption = 'male';

  bodyFatPercent: number | null = null;

  readonly walkMiles = 1;
  readonly runMiles = 1;

  readonly walkSpeedMph = 3;
  readonly runSpeedMph = 6;

  results: TrackerResults | null = null;
  errorMessage = '';

  whoopConnected = false;
  whoopBusy = false;
  whoopErrorMessage = '';
  whoopStatusMessage = '';
  whoopProfile: WhoopProfile | null = null;
  whoopBodyMeasurement: WhoopBodyMeasurement | null = null;
  whoopWorkouts: WhoopWorkoutItem[] = [];
  whoopTotals = {
    workoutCount: 0,
    standardCalories: 0,
    adjustedCalories: 0
  };

  private whoopPopup: Window | null = null;

  private readonly WHOOP_APP_USER_ID_KEY = 'clarichat.whoop.appUserId';
  private readonly WHOOP_CONNECTED_KEY = 'clarichat.whoop.connected';

  private readonly oauthMessageHandler = (event: MessageEvent): void => {
    if (event.origin !== this.whoopService.getBackendOrigin()) {
      return;
    }

    if (!event.data || event.data.source !== 'whoop-oauth') {
      return;
    }

    if (event.data.error) {
      this.whoopBusy = false;
      this.whoopErrorMessage = event.data.errorDescription || event.data.error || 'WHOOP authorization failed.';
      this.whoopStatusMessage = '';
      return;
    }

    const code = String(event.data.code || '').trim();
    const state = String(event.data.state || '').trim();

    if (!code || !state) {
      this.whoopBusy = false;
      this.whoopErrorMessage = 'WHOOP did not return a valid authorization code.';
      return;
    }

    this.completeWhoopExchange(code, state);
  };

  constructor(private whoopService: WhoopCalibrationService) {
    this.recalculateLocalOnly();
  }

  ngOnInit(): void {
    window.addEventListener('message', this.oauthMessageHandler);

    this.whoopConnected = localStorage.getItem(this.WHOOP_CONNECTED_KEY) === 'true';
    if (this.whoopConnected) {
      this.loadWhoopWorkoutData();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.oauthMessageHandler);
    if (this.whoopPopup && !this.whoopPopup.closed) {
      this.whoopPopup.close();
    }
  }

  recalculate(): void {
    this.recalculateLocalOnly();

    if (this.whoopConnected) {
      this.loadWhoopWorkoutData();
    }
  }

  reset(): void {
    this.heightFeet = 5;
    this.heightInches = 8;
    this.weightLbs = 170;
    this.age = 35;
    this.gender = 'male';
    this.bodyFatPercent = null;
    this.errorMessage = '';
    this.whoopErrorMessage = '';
    this.whoopStatusMessage = '';
    this.recalculate();
  }

  connectWhoop(): void {
    this.whoopBusy = true;
    this.whoopErrorMessage = '';
    this.whoopStatusMessage = 'Opening WHOOP authorization...';

    this.whoopService.getAuthUrl(this.getOrCreateAppUserId(), window.location.origin).subscribe({
      next: (response) => {
        const popupWidth = 540;
        const popupHeight = 720;
        const left = Math.max((window.screen.width - popupWidth) / 2, 0);
        const top = Math.max((window.screen.height - popupHeight) / 2, 0);

        this.whoopPopup = window.open(
          response.authUrl,
          'whoopOAuth',
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!this.whoopPopup) {
          this.whoopBusy = false;
          this.whoopErrorMessage = 'Popup blocked. Please allow popups for this site and try again.';
          this.whoopStatusMessage = '';
          return;
        }

        this.whoopStatusMessage = 'Waiting for WHOOP authorization...';
      },
      error: (error) => {
        this.whoopBusy = false;
        this.whoopStatusMessage = '';
        this.whoopErrorMessage = error?.error?.message || 'Unable to start WHOOP authorization.';
      }
    });
  }

  disconnectWhoop(): void {
    this.whoopBusy = true;
    this.whoopErrorMessage = '';
    this.whoopStatusMessage = 'Disconnecting WHOOP...';

    this.whoopService.disconnectWhoop(this.getOrCreateAppUserId()).subscribe({
      next: () => {
        this.whoopBusy = false;
        this.whoopConnected = false;
        this.whoopProfile = null;
        this.whoopBodyMeasurement = null;
        this.whoopWorkouts = [];
        this.whoopTotals = {
          workoutCount: 0,
          standardCalories: 0,
          adjustedCalories: 0
        };
        this.whoopStatusMessage = 'WHOOP disconnected.';
        localStorage.removeItem(this.WHOOP_CONNECTED_KEY);
      },
      error: (error) => {
        this.whoopBusy = false;
        this.whoopErrorMessage = error?.error?.message || 'Unable to disconnect WHOOP.';
        this.whoopStatusMessage = '';
      }
    });
  }

  loadWhoopWorkoutData(): void {
    if (!this.whoopConnected) {
      return;
    }

    this.whoopBusy = true;
    this.whoopErrorMessage = '';
    this.whoopStatusMessage = 'Loading WHOOP workouts and recalculating calories...';

    this.whoopService.recalculateWhoopWorkouts({
      appUserId: this.getOrCreateAppUserId(),
      age: this.age,
      gender: this.gender,
      bodyFatPercent: this.bodyFatPercent,
      limit: 10
    }).subscribe({
      next: (response: WhoopWorkoutResponse) => {
        this.whoopBusy = false;
        this.whoopConnected = true;
        this.whoopProfile = response.profile || null;
        this.whoopBodyMeasurement = response.bodyMeasurement || null;
        this.whoopWorkouts = response.workouts || [];
        this.whoopTotals = response.totals || {
          workoutCount: 0,
          standardCalories: 0,
          adjustedCalories: 0
        };

        if (response.bodyMeasurement) {
          this.syncFromWhoopBodyMeasurement(response.bodyMeasurement);
          this.recalculateLocalOnly();
        }

        this.whoopStatusMessage = this.whoopWorkouts.length
          ? `Loaded ${this.whoopWorkouts.length} WHOOP workouts.`
          : 'WHOOP connected, but no recent workouts were returned.';
        localStorage.setItem(this.WHOOP_CONNECTED_KEY, 'true');
      },
      error: (error) => {
        this.whoopBusy = false;
        this.whoopErrorMessage = error?.error?.message || 'Unable to load WHOOP workout data.';
        this.whoopStatusMessage = '';
      }
    });
  }

  round(value: number): number {
    return Math.round(value);
  }

  oneDecimal(value: number): string {
    return Number(value || 0).toFixed(1);
  }

  twoDecimals(value: number): string {
    return Number(value || 0).toFixed(2);
  }

  private completeWhoopExchange(code: string, state: string): void {
    this.whoopBusy = true;
    this.whoopErrorMessage = '';
    this.whoopStatusMessage = 'Finalizing WHOOP connection...';

    this.whoopService.exchangeCode({
      appUserId: this.getOrCreateAppUserId(),
      code,
      state
    }).subscribe({
      next: (response) => {
        this.whoopConnected = !!response.connected;
        this.whoopProfile = response.profile || null;
        this.whoopBodyMeasurement = response.bodyMeasurement || null;

        if (response.bodyMeasurement) {
          this.syncFromWhoopBodyMeasurement(response.bodyMeasurement);
        }

        localStorage.setItem(this.WHOOP_CONNECTED_KEY, 'true');

        this.whoopStatusMessage = response.profile
          ? `WHOOP connected for ${response.profile.firstName} ${response.profile.lastName}.`
          : 'WHOOP connected successfully.';

        this.recalculate();
      },
      error: (error) => {
        this.whoopBusy = false;
        this.whoopStatusMessage = '';
        this.whoopErrorMessage = error?.error?.message || 'WHOOP authorization exchange failed.';
      }
    });
  }

  private recalculateLocalOnly(): void {
    this.errorMessage = '';

    if (!this.isValidInput()) {
      this.results = null;
      return;
    }

    const totalInches = (this.heightFeet * 12) + this.heightInches;
    const heightMeters = totalInches * 0.0254;
    const weightKg = this.weightLbs * 0.45359237;

    const bmi = weightKg / (heightMeters * heightMeters);

    const effectiveBodyFat =
      this.bodyFatPercent != null && this.bodyFatPercent >= 3 && this.bodyFatPercent <= 70
        ? this.bodyFatPercent
        : this.estimateBodyFatPercent(bmi, this.age, this.gender);

    const usedEstimatedBodyFat =
      this.bodyFatPercent == null || this.bodyFatPercent < 3 || this.bodyFatPercent > 70;

    const leanMassLbs = this.weightLbs * (1 - (effectiveBodyFat / 100));
    const fatMassLbs = this.weightLbs - leanMassLbs;

    const walkCaloriesBase = this.calculateActivityCalories({
      weightKg,
      speedMph: this.walkSpeedMph,
      distanceMiles: this.walkMiles,
      mode: 'walk'
    });

    const runCaloriesBase = this.calculateActivityCalories({
      weightKg,
      speedMph: this.runSpeedMph,
      distanceMiles: this.runMiles,
      mode: 'run'
    });

    const recalibrationFactor = this.calculateRecalibrationFactor({
      bmi,
      gender: this.gender,
      bodyFatPercent: effectiveBodyFat,
      leanMassRatio: leanMassLbs / this.weightLbs
    });

    const walkCaloriesAdjusted = walkCaloriesBase * recalibrationFactor;
    const runCaloriesAdjusted = runCaloriesBase * recalibrationFactor;

    this.results = {
      bmi,
      bodyFatPercent: effectiveBodyFat,
      usedEstimatedBodyFat,
      leanMassLbs,
      fatMassLbs,

      walkCaloriesBase,
      runCaloriesBase,
      totalCaloriesBase: walkCaloriesBase + runCaloriesBase,

      walkCaloriesAdjusted,
      runCaloriesAdjusted,
      totalCaloriesAdjusted: walkCaloriesAdjusted + runCaloriesAdjusted,

      recalibrationFactor
    };
  }

  private getOrCreateAppUserId(): string {
    const existing = localStorage.getItem(this.WHOOP_APP_USER_ID_KEY);
    if (existing) {
      return existing;
    }

    const generated =
      (window.crypto && 'randomUUID' in window.crypto && window.crypto.randomUUID())
      || `whoop-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

    localStorage.setItem(this.WHOOP_APP_USER_ID_KEY, generated);
    return generated;
  }

  private syncFromWhoopBodyMeasurement(bodyMeasurement: WhoopBodyMeasurement): void {
    const heightMeter = Number(bodyMeasurement.heightMeter || 0);
    const weightKg = Number(bodyMeasurement.weightKilogram || 0);

    if (heightMeter > 0) {
      const totalInches = heightMeter / 0.0254;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches - (feet * 12));

      this.heightFeet = feet;
      this.heightInches = Math.min(Math.max(inches, 0), 11);
    }

    if (weightKg > 0) {
      this.weightLbs = Math.round(weightKg / 0.45359237);
    }
  }

  private isValidInput(): boolean {
    if (this.heightFeet < 3 || this.heightFeet > 8) {
      this.errorMessage = 'Height feet should be between 3 and 8.';
      return false;
    }

    if (this.heightInches < 0 || this.heightInches > 11) {
      this.errorMessage = 'Height inches should be between 0 and 11.';
      return false;
    }

    if (this.weightLbs < 50 || this.weightLbs > 700) {
      this.errorMessage = 'Weight should be between 50 and 700 pounds.';
      return false;
    }

    if (this.age < 18 || this.age > 100) {
      this.errorMessage = 'Age should be between 18 and 100.';
      return false;
    }

    if (this.bodyFatPercent != null && (this.bodyFatPercent < 3 || this.bodyFatPercent > 70)) {
      this.errorMessage = 'Body fat % should be between 3 and 70, or leave it blank.';
      return false;
    }

    return true;
  }

  private estimateBodyFatPercent(bmi: number, age: number, gender: GenderOption): number {
    const sexFlag = gender === 'male' ? 1 : 0;
    const estimated = (1.20 * bmi) + (0.23 * age) - (10.8 * sexFlag) - 5.4;
    return this.clamp(estimated, 6, 65);
  }

  private calculateActivityCalories(params: {
    weightKg: number;
    speedMph: number;
    distanceMiles: number;
    mode: 'walk' | 'run';
  }): number {
    const speedMetersPerMinute = params.speedMph * 26.8224;
    const grade = 0;

    const vo2 =
      params.mode === 'walk'
        ? (0.1 * speedMetersPerMinute) + (1.8 * speedMetersPerMinute * grade) + 3.5
        : (0.2 * speedMetersPerMinute) + (0.9 * speedMetersPerMinute * grade) + 3.5;

    const durationMinutes = (params.distanceMiles / params.speedMph) * 60;
    const caloriesPerMinute = (vo2 * params.weightKg) / 200;

    return caloriesPerMinute * durationMinutes;
  }

  private calculateRecalibrationFactor(params: {
    bmi: number;
    gender: GenderOption;
    bodyFatPercent: number;
    leanMassRatio: number;
  }): number {
    const referenceBodyFat = params.gender === 'male' ? 20 : 30;
    const referenceLeanMassRatio = 1 - (referenceBodyFat / 100);

    const bodyFatModifier =
      1 - (((params.bodyFatPercent - referenceBodyFat) / 100) * 0.35);

    const leanMassModifier =
      1 + ((params.leanMassRatio - referenceLeanMassRatio) * 0.40);

    let bmiModifier = 1;

    if (params.bmi > 25) {
      bmiModifier -= Math.min((params.bmi - 25) * 0.003, 0.08);
    } else if (params.bmi < 18.5) {
      bmiModifier -= Math.min((18.5 - params.bmi) * 0.002, 0.03);
    }

    return this.clamp(bodyFatModifier * leanMassModifier * bmiModifier, 0.72, 1.08);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}